import { ConfirmPartialBookingDetailModel } from '@/__generated';
import Spin from '@/components/Spin';
import { appDisplayDateFormat } from '@/config';
import { HotelRoomStatus } from '@/constants';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { handleApiUnKnowError, handleBookingNotFound, handleSubmitError } from '@/helpers/errorHandle';
import { formatPriceWithCurrency } from '@/hooks/useCurrencyFormat';
import { apiSdk } from '@/httpClient';
import bookingService from '@/services/bookingService';
import { AppUrls } from '@/urls';
import { faCheckCircle, faClose, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

type Props = {
  bookingId: string;
  onClose: () => void;
  defaultConfirm: boolean;
};

export default function PartialBookedConfirmModal({ bookingId, onClose, defaultConfirm }: Props) {
  const [loading, setLoading] = React.useState<boolean>(true);
  const history = useHistory();
  const { t } = useTranslation();
  const [bookingDetail, setBookingDetail] = React.useState<ConfirmPartialBookingDetailModel>();
  const [isConfirmAllRooms, setConfirmAllRooms] = React.useState<boolean>(defaultConfirm);

  React.useEffect(() => {
    window.onbeforeunload = function() {
      return 'Please confirm partial booking before close this tab, If you close this tab, please go to agent bookings to confirm booking!';
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, []);
  const getPartialBookingDetail = () => {
    if (bookingId) {
      setLoading(true);
      apiSdk.BookingService.getPartialBookingDetail({ bookingId: bookingId })
        .then(res => {
          if (res) {
            setBookingDetail(res);
          } else {
            handleBookingNotFound();
          }
        })
        .catch(handleBookingNotFound)
        .finally(() => {
          setLoading(false);
        });
    }
  };
  React.useEffect(() => {
    getPartialBookingDetail();
  }, [bookingId]);

  const confirm = () => {
    if (isConfirmAllRooms) {
      setLoading(true);
      apiSdk.BookingService.continuePartialBooking({ bookingId: bookingId })
        .then(res => {
          bookingService.handleConfirmPartialBooking(bookingId, res);
        })
        .catch(handleApiUnKnowError)
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      apiSdk.BookingService.cancelPartialBooking({
        body: {
          bookingId: bookingId,
          cancellationFee: bookingDetail?.totalCancellationFee || 0,
        },
      })
        .then(res => {
          bookingService.handleCancelBookingResponse(bookingId, res, getPartialBookingDetail);
        })
        .catch(handleApiUnKnowError)
        .finally(() => setLoading(false));
    }
  };

  return (
    <Modal isOpen={true} unmountOnClose={true}>
      <ModalHeader>Important Notification</ModalHeader>
      <ModalBody>
        <Spin loading={loading}>
          {bookingDetail ? (
            <div>
              <div className="mb-10">
                <span>
                  Due to limited availability, not all the rooms in <b>{bookingDetail.hotelName}</b> have been booked successfully.
                </span>
                <div className="y-gap-10 text-14 mt-10">
                  {bookingDetail.rooms?.map((room, i) => {
                    const isBooked = room.status === HotelRoomStatus.Booked;
                    return (
                      <div
                        className={`row x-gap-5 d-flex items-center mt-5 rounded pt-10 pb-10 px-10 ${isBooked ? 'bg-success-1' : 'bg-red-3'}`}
                        key={i}
                      >
                        <div className="col-auto">
                          {isBooked ? (
                            <FontAwesomeIcon icon={faCheckCircle} className="text-20 text-green-2" />
                          ) : (
                            <FontAwesomeIcon icon={faClose} className="text-20 text-red-1" />
                          )}
                        </div>
                        <div className="col ml-10">
                          <div className="fw-500">{`[${room.roomTypeCategory}] ${room.roomTypeName}`}</div>
                          <div>
                            Price:<span className="fw-500 text-dark-1 ml-5">{formatPriceWithCurrency(room.price, bookingDetail.currency)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className=" mt-10 py-10 mb-10">
                <span className="text-red-1 fw-500">Please choose one option to continue:</span>
                <div className="sidebar-checkbox">
                  <div className="row y-gap-10 items-center justify-between">
                    <div className="col ">
                      <div className="form-checkbox d-flex items-center">
                        <input type="checkbox" checked={isConfirmAllRooms} onChange={() => setConfirmAllRooms(true)} />
                        <div className={`form-checkbox__mark rounded-full w-20px h-20px`}>
                          <div className="form-checkbox__icon icon-check" />
                        </div>
                        <div className="text-15 ml-10">
                          <div className="fw-500">Proceed with the successful room(s)</div>
                          <span>
                            Payment Due:
                            <span className="fw-500 text-dark-1 ml-5">
                              {formatPriceWithCurrency(bookingDetail.sellingPrice, bookingDetail.currency)}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="sidebar-checkbox">
                  <div className="row y-gap-10 items-center justify-between">
                    <div className="col ">
                      <div className="form-checkbox d-flex items-center">
                        <input type="checkbox" checked={!isConfirmAllRooms} onChange={() => setConfirmAllRooms(false)} />
                        <div className={`form-checkbox__mark rounded-full w-20px h-20px`}>
                          <div className="form-checkbox__icon icon-check" />
                        </div>
                        <div className="text-15 ml-10 ">
                          <div className="fw-500">Cancel all rooms</div>
                          <div>
                            Total cancellation fee:
                            <span className="fw-500 text-dark-1 ml-5">
                              {formatPriceWithCurrency(bookingDetail.totalCancellationFee, bookingDetail.currency)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-top mt-10 pt-10">
                <div className="col-12 d-flex justify-end">
                  <button type="submit" className="button px-15 bg-blue-1  py-10 text-white" onClick={confirm}>
                    <i className="icon-check text-12 px-5" />
                    Submit
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ minHeight: 300 }}></div>
          )}
        </Spin>
      </ModalBody>
    </Modal>
  );
}
